import { useState, useRef, useEffect } from 'react';
import useScrollAnimation from '../hooks/useScrollAnimation';

const BrandVideo = () => {
  const { ref: sectionRef, animationClass } = useScrollAnimation('fade-in-up', 0.1);
  const videos = [
    { id: 'pU5zrzlNMZs', title: 'Behind the Scenes' },
    { id: 'Ehh4WUqWg88', title: 'Customer Stories' },
    { id: 'm5YxyrDtqg4', title: 'The Making Process' },
  ];

  const [currentIndex, setCurrentIndex] = useState(0);
  const [startX, setStartX] = useState(0);
  const [currentX, setCurrentX] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const containerRef = useRef(null);

  // Get ordered cards for stack effect with left and right visible
  const getOrderedCards = () => {
    const total = videos.length;
    const prevIndex = (currentIndex - 1 + total) % total;
    const nextIndex = (currentIndex + 1) % total;

    return [
      { ...videos[prevIndex], position: 'left', zIndex: 10 },
      { ...videos[currentIndex], position: 'center', zIndex: 30 },
      { ...videos[nextIndex], position: 'right', zIndex: 10 },
    ];
  };

  const handleSwipe = (direction) => {
    if (direction === 'left') {
      // Swipe left = next video
      setCurrentIndex((prev) => (prev + 1) % videos.length);
    } else if (direction === 'right') {
      // Swipe right = previous video
      setCurrentIndex((prev) => (prev - 1 + videos.length) % videos.length);
    }
  };

  // Touch events
  const handleTouchStart = (e) => {
    setStartX(e.touches[0].clientX);
    setIsDragging(true);
  };

  const handleTouchMove = (e) => {
    if (!isDragging) return;
    setCurrentX(e.touches[0].clientX - startX);
  };

  const handleTouchEnd = () => {
    if (Math.abs(currentX) > 100) {
      handleSwipe(currentX > 0 ? 'right' : 'left');
    }
    setIsDragging(false);
    setCurrentX(0);
    setStartX(0);
  };

  // Mouse events for desktop
  const handleMouseDown = (e) => {
    setStartX(e.clientX);
    setIsDragging(true);
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    setCurrentX(e.clientX - startX);
  };

  const handleMouseUp = () => {
    if (Math.abs(currentX) > 100) {
      handleSwipe(currentX > 0 ? 'right' : 'left');
    }
    setIsDragging(false);
    setCurrentX(0);
    setStartX(0);
  };

  const handleMouseLeave = () => {
    if (isDragging) {
      handleMouseUp();
    }
  };

  // Auto-rotate every 5 seconds if not interacting
  useEffect(() => {
    if (!isDragging) {
      const interval = setInterval(() => {
        setCurrentIndex((prev) => (prev + 1) % videos.length);
      }, 5000);
      return () => clearInterval(interval);
    }
  }, [isDragging, videos.length]);

  const orderedCards = getOrderedCards();

  return (
    <section ref={sectionRef} className={`bg-white py-12 sm:py-16 md:py-20 lg:py-24 px-4 sm:px-6 overflow-hidden ${animationClass}`}>
      <div className="max-w-screen-xl mx-auto">
        {/* Section Header */}
        <div className="text-center mb-8 sm:mb-12">
          <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-serif font-bold text-gray-900">
            Watch the <span className="bg-[#E85D44] text-[#FFD93D] px-4 sm:px-6 py-1 sm:py-2 inline-block">Magic</span>
          </h2>
        </div>

        {/* Desktop View - 3 videos side by side */}
        <div className="hidden lg:grid lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
          {videos.map((video, index) => (
            <div
              key={index}
              className="relative bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl overflow-hidden shadow-xl hover:shadow-2xl transition-shadow duration-300 mx-auto w-full"
              style={{ aspectRatio: '9/16' }}
            >
              {/* Video Title at Top */}
              <div className="absolute top-0 left-0 right-0 z-40 bg-gradient-to-b from-black/80 to-transparent p-4">
                <h3 className="text-white text-lg font-bold text-center">
                  {video.title}
                </h3>
              </div>

              {/* YouTube Embed - Square format */}
              <iframe
                src={`https://www.youtube.com/embed/${video.id}?autoplay=1&mute=1&loop=1&playlist=${video.id}&controls=0&modestbranding=1&rel=0&fs=0&disablekb=1&iv_load_policy=3&playsinline=1&showinfo=0`}
                title={video.title}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                loading="lazy"
                className="absolute inset-0 w-full h-full"
                style={{ border: 'none', pointerEvents: 'none' }}
              />

              {/* Overlay to completely hide YouTube UI */}
              <div className="absolute inset-0 pointer-events-none z-10">
                <div className="absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-t from-gray-900"></div>
              </div>
            </div>
          ))}
        </div>

        {/* Mobile View - Tinder-style Card Stack */}
        <div className="lg:hidden relative w-full max-w-sm mx-auto" style={{ aspectRatio: '9/16' }}>
          <div
            ref={containerRef}
            className="relative w-full h-full"
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseLeave}
          >
            {orderedCards.map((video, index) => {
              const isCenter = video.position === 'center';
              const isLeft = video.position === 'left';
              const isRight = video.position === 'right';

              // Center card follows drag
              const rotation = isCenter && isDragging ? currentX / 20 :
                               isLeft ? -8 : isRight ? 8 : 0;
              const translateX = isCenter && isDragging ? currentX :
                                isLeft ? -120 : isRight ? 120 : 0;
              const scale = isCenter ? 1 : 0.85;
              const translateY = isCenter ? 0 : 30;
              const opacity = isCenter ? 1 : 0.6;

              return (
                <div
                  key={`${video.id}-${index}`}
                  className="absolute inset-0 transition-all duration-300"
                  style={{
                    zIndex: video.zIndex,
                    transform: `translateX(${translateX}px) translateY(${translateY}px) rotate(${rotation}deg) scale(${scale})`,
                    opacity: opacity,
                    transformOrigin: 'center center',
                  }}
                >
                  <div className="relative bg-gradient-to-br from-gray-900 to-gray-800 rounded-3xl overflow-hidden shadow-2xl w-full h-full">
                    {/* Video Title at Top */}
                    <div className="absolute top-0 left-0 right-0 z-40 bg-gradient-to-b from-black/80 to-transparent p-6">
                      <h3 className="text-white text-xl sm:text-2xl font-bold text-center">
                        {video.title}
                      </h3>
                    </div>

                    {/* YouTube Embed - Square format */}
                    <iframe
                      src={`https://www.youtube.com/embed/${video.id}?autoplay=1&mute=1&loop=1&playlist=${video.id}&controls=0&modestbranding=1&rel=0&fs=0&disablekb=1&iv_load_policy=3&playsinline=1&showinfo=0`}
                      title={video.title}
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      loading="lazy"
                      className="absolute inset-0 w-full h-full"
                      style={{ border: 'none', pointerEvents: 'none' }}
                    />

                    {/* Overlay to completely hide YouTube UI */}
                    <div className="absolute inset-0 pointer-events-none z-10">
                      <div className="absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-t from-gray-900"></div>
                    </div>

                    {/* Swipe Indicators on center card */}
                    {isCenter && (
                      <>
                        {isDragging && currentX > 50 && (
                          <div className="absolute top-1/2 left-6 -translate-y-1/2 z-50 bg-[#E85D44] text-white px-4 py-2 rounded-full font-bold text-sm rotate-[-15deg] shadow-xl">
                            PREVIOUS
                          </div>
                        )}
                        {isDragging && currentX < -50 && (
                          <div className="absolute top-1/2 right-6 -translate-y-1/2 z-50 bg-[#E85D44] text-white px-4 py-2 rounded-full font-bold text-sm rotate-[15deg] shadow-xl">
                            NEXT
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Swipe Hint */}
          <div className="absolute -bottom-12 left-1/2 -translate-x-1/2 text-center">
            <p className="text-gray-500 text-sm animate-pulse">
              ← Swipe to see more →
            </p>
          </div>

          {/* Navigation Dots */}
          <div className="absolute -bottom-20 left-1/2 -translate-x-1/2 flex gap-2">
            {videos.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentIndex(index)}
                className={`w-3 h-3 rounded-full transition-all duration-300 ${
                  index === currentIndex
                    ? 'bg-[#E85D44] w-8'
                    : 'bg-gray-300 hover:bg-gray-400'
                }`}
                aria-label={`Go to video ${index + 1}`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default BrandVideo;
